// src/services/kpiService.js
import { collection, query, where, getDocs, getCountFromServer, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Servicio encargado de la lógica de negocio y consultas a Firestore para el Dashboard.
 * Centralizar esto permite:
 * 1. Cambiar la lógica en un solo lugar.
 * 2. Optimizar consultas (ahorro de dinero y velocidad).
 * 3. Mantener los componentes de React limpios.
 */
export const kpiService = {

    /**
     * Obtiene todos los indicadores principales y agregados para el Dashboard Avanzado.
     * @param {string} userId - ID del usuario actual (opcional para futuros filtros).
     * @returns {Promise<Object>} Objeto con KPIs básicos y datos para gráficos.
     */
    getDashboardStats: async (user) => {
        const stats = {
            // KPIs Básicos
            visitsToday: 0,
            pendingQuotes: 0,
            pendingPayment: 0,
            // Datos para Gráficos
            techRanking: [],       
            quoteFunnel: [],       
            financialSummary: []   
        };

        if (!user) return stats;

        const isAdmin = user.role === 'admin' || user.role === 'superuser';
        const perms = user.permissions || {};

        // Definimos accesos basados en los permisos de los módulos que proveen la data
        const canCalendar = isAdmin || perms.calendar;
        const canQuotes = isAdmin || perms.quotes;
        const canAccounting = isAdmin || perms.accounting;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);

        // 1. VISITAS DE HOY
        if (canCalendar) {
            try {
                const visitsRef = collection(db, 'visits');
                const visitsTodayQuery = query(
                    visitsRef, 
                    where('fecha', '>=', Timestamp.fromDate(today)),
                    where('fecha', '<', Timestamp.fromDate(tomorrow))
                );
                const visitsTodaySnap = await getCountFromServer(visitsTodayQuery);
                stats.visitsToday = visitsTodaySnap.data().count;
            } catch (e) {
                console.warn('Dashboard KPI - Visitas Hoy error:', e.code);
            }
        }

        // 2. COTIZACIONES
        if (canQuotes) {
            try {
                const quotesRef = collection(db, 'quotes');
                const [quotesPendingSnap, quotesApprovedSnap, quotesRejectedSnap] = await Promise.all([
                    getCountFromServer(query(quotesRef, where('status', 'in', ['Pendiente', 'Enviada']))),
                    getCountFromServer(query(quotesRef, where('status', '==', 'Aprobada'))),
                    getCountFromServer(query(quotesRef, where('status', '==', 'Rechazada')))
                ]);

                stats.pendingQuotes = quotesPendingSnap.data().count;
                stats.quoteFunnel = [
                    { name: 'Aprobadas', value: quotesApprovedSnap.data().count, fill: '#10B981' },
                    { name: 'Pendientes', value: stats.pendingQuotes, fill: '#F59E0B' },
                    { name: 'Rechazadas', value: quotesRejectedSnap.data().count, fill: '#EF4444' }
                ];
            } catch (e) {
                console.warn('Dashboard KPI - Cotizaciones error:', e.code);
            }
        }

        // 3. RANKING DE TÉCNICOS
        if (canCalendar) {
            try {
                const visitsRef = collection(db, 'visits');
                const visitsMonthQuery = query(
                    visitsRef,
                    where('fecha', '>=', Timestamp.fromDate(startOfMonth)),
                    where('fecha', '<=', Timestamp.fromDate(endOfMonth))
                );
                const visitsMonthSnap = await getDocs(visitsMonthQuery);
                
                const techCounts = {};
                visitsMonthSnap.docs.forEach(doc => {
                    const techName = doc.data().tecnico || 'Sin Asignar';
                    techCounts[techName] = (techCounts[techName] || 0) + 1;
                });

                stats.techRanking = Object.entries(techCounts)
                    .map(([name, value]) => ({ name, value }))
                    .sort((a, b) => b.value - a.value)
                    .slice(0, 5);
            } catch (e) {
                console.warn('Dashboard KPI - Ranking Técnicos error:', e.code);
            }
        }

        // 4. FINANZAS Y DEUDA
        if (canAccounting) {
            try {
                const accountingRef = collection(db, 'accounting');
                const accSnap = await getDocs(accountingRef);

                let totalDebt = 0;
                let ingresosMes = 0;

                accSnap.docs.forEach(doc => {
                    const data = doc.data();
                    const total = parseFloat(data.totalAmount) || 0;
                    let realPaid = 0;

                    if (data.payments && Array.isArray(data.payments)) {
                        realPaid = data.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                        data.payments.forEach(p => {
                            let pDate;
                            if (p.date?.seconds) pDate = new Date(p.date.seconds * 1000);
                            else if (p.date instanceof Date) pDate = p.date;
                            else pDate = new Date(p.date + "T12:00:00");

                            if (pDate >= startOfMonth && pDate <= endOfMonth) {
                                ingresosMes += (parseFloat(p.amount) || 0);
                            }
                        });
                    } else {
                        realPaid = parseFloat(data.paidAmount) || 0;
                    }

                    if (total - realPaid > 1) {
                        totalDebt += (total - realPaid);
                    }
                });
                stats.pendingPayment = totalDebt;

                const expensesRef = collection(db, 'expenses');
                const allExpensesSnap = await getDocs(expensesRef);
                let egresosMes = 0;
                
                allExpensesSnap.docs.forEach(doc => {
                    const data = doc.data();
                    let eDate;
                    if (data.date?.seconds) eDate = new Date(data.date.seconds * 1000);
                    else if (data.date instanceof Date) eDate = data.date;
                    else eDate = new Date(data.date + "T12:00:00");

                    if (eDate >= startOfMonth && eDate <= endOfMonth) {
                        egresosMes += (parseFloat(data.amount) || 0);
                    }
                });

                const mesActualName = startOfMonth.toLocaleString('es-CL', { month: 'short' }).toUpperCase();
                stats.financialSummary = [
                    { name: mesActualName, Ingresos: ingresosMes, Egresos: egresosMes }
                ];
            } catch (e) {
                console.warn('Dashboard KPI - Finanzas error:', e.code);
            }
        }

        return stats;
    }
};